/**
 * Front-end helpers for the BFF's MCP forwarder. The browser never holds the MCP key
 * or talks to the MCP directly — these just call /api/mcp/* on our own origin.
 *
 * THE NAME A PAGE PASSES IS AN ACTION, NOT A TOOL — corrected 2026-09-03.
 *
 * Every page here still calls `mcpCall('listDealers')`, `mcpWrite('setSkuPrice')` and so
 * on: the per-handler names the MCP had until the 2026-08-27 consolidation, which folded
 * 187 tools into fifteen domain tools (`cabana_dealer`, `cabana_config`, …) that each take
 * an `action` parameter. The BFF was updated with it and gates on that action — "No
 * action given; this route permits read actions only." — and the SPA was not. Every
 * console page has failed its data calls with that 403 since; the tier-gate build was
 * already like this. Found on 2026-09-03 by signing in and walking the pages.
 *
 * The resolution lives HERE rather than in thirteen pages: a page keeps naming what it
 * wants done, and this module turns that into the domain tool and snake_case action
 * the MCP now speaks. The mapping is not hard-coded — it is read from the tools list
 * the BFF already serves, in which each domain tool's input schema enumerates its
 * actions. An action no tool lists is refused here with a 404-shaped ApiError, which is
 * the same outcome the BFF would give and arrives before a request is spent.
 */
import { api, ApiError } from './api';
import { getCsrfToken, clearCsrfToken } from './csrf';

export interface McpToolInfo {
  name: string;
  description?: string;
  inputSchema?: unknown;
}

/** The tools the signed-in operator's role may call — drives which UI actions appear. */
export async function mcpListTools(): Promise<McpToolInfo[]> {
  const { tools } = await api<{ tools: McpToolInfo[] }>('/mcp/tools');
  return tools;
}

/** camelCase handler name → snake_case action name, the same rule the MCP applies. */
export function actionName(handlerName: string): string {
  return handlerName
    .replace(/([a-z0-9])([A-Z])/g, '$1_$2')
    .replace(/([A-Z]+)([A-Z][a-z])/g, '$1_$2')
    .toLowerCase();
}

export interface ResolvedAction {
  tool: string;
  action: string;
}

/** action name → the domain tool that offers it, built from the tools list. */
export function indexActions(tools: McpToolInfo[]): Map<string, string> {
  const map = new Map<string, string>();
  for (const t of tools) {
    const schema = t.inputSchema as { properties?: { action?: { enum?: unknown } } } | undefined;
    const actions = schema?.properties?.action?.enum;
    if (!Array.isArray(actions)) continue;
    for (const a of actions) if (typeof a === 'string' && !map.has(a)) map.set(a, t.name);
  }
  return map;
}

let actionIndex: Promise<Map<string, string>> | null = null;

/** The action index, fetched once per page load and shared by every call. */
export function loadActionIndex(force = false): Promise<Map<string, string>> {
  if (!actionIndex || force) {
    actionIndex = mcpListTools()
      .then(indexActions)
      .catch((err) => {
        actionIndex = null; // do not cache a failure
        throw err;
      });
  }
  return actionIndex;
}

/**
 * Resolve a page's name — old camelCase handler name or already-snake_case action —
 * to the domain tool and action the MCP speaks. Returns null when no tool the operator
 * may call offers it, which is how a control for a cancelled or ungranted action hides.
 */
export async function resolveAction(name: string): Promise<ResolvedAction | null> {
  const index = await loadActionIndex();
  const action = name.includes('_') ? name : actionName(name);
  const tool = index.get(action);
  return tool ? { tool, action } : null;
}

async function resolveOrThrow(name: string): Promise<ResolvedAction> {
  const r = await resolveAction(name);
  if (!r) throw new ApiError(404, `"${name}" is not an action available to you.`, 'action_not_available');
  return r;
}

/** Call one read action through the BFF and return its payload. */
export async function mcpCall<T = unknown>(
  name: string,
  args: Record<string, unknown> = {},
): Promise<T> {
  const { tool, action } = await resolveOrThrow(name);
  const { result } = await api<{ result: T }>('/mcp/call', {
    method: 'POST',
    body: JSON.stringify({ name: tool, arguments: { action, ...args } }),
  });
  return result;
}

/**
 * Call one allowlisted write action through the BFF's CSRF-protected /mcp/write route.
 * Sends the cached CSRF token; if the server reports it expired (403 invalid_csrf_token —
 * e.g. the session rotated), refetch the token once and retry. Any other error propagates.
 */
export async function mcpWrite<T = unknown>(
  name: string,
  args: Record<string, unknown> = {},
): Promise<T> {
  const { tool, action } = await resolveOrThrow(name);
  const send = (token: string) =>
    api<{ result: T }>('/mcp/write', {
      method: 'POST',
      headers: { 'x-csrf-token': token },
      body: JSON.stringify({ name: tool, arguments: { action, ...args } }),
    });
  try {
    return (await send(await getCsrfToken())).result;
  } catch (err) {
    if (err instanceof ApiError && err.status === 403 && err.code === 'invalid_csrf_token') {
      clearCsrfToken();
      return (await send(await getCsrfToken(true))).result;
    }
    throw err;
  }
}
